// Eszköz-IGÉNY sáv — EGY közös jobb oldali beúszó három móddal (Ilcsa #116, #119):
//   • LÉTREHOZÁS (`requestId == null`): az igénylés beadása (POST), a beadott igény `status=requested`;
//   • NÉZET: az igény minden adata olvashatóan (a mélylink — levél, harang — ide érkezik);
//   • SZERKESZTÉS: ugyanaz az űrlap előtöltve (PATCH `If-Match`/OCC). Ki szerkeszthet: az igénylő a SAJÁT igényét
//     a döntésig, a bíráló (asset_requests.decide) a beérkezésig — a tükör a `request-edit.ts`; a szerver a fal.
// A validálás forrás-igazsága a @atlas/contracts (Create/UpdateBodySchema, a resolver a features/assets/form.ts-en
// át). A kategória CSAK UI-szűrő a típus-listához (a katalógusból), a body-ba NEM megy. A „kinek" iroda VAGY
// személy (a csapatot a szerver számolja). Az igényt id alapján töltjük (a lista betöltött lapjától függetlenül),
// a lista-sor csak az azonnali megjelenítés magja (`seed`). Az írás optimista: a detail-cache azonnal átíródik,
// hibánál visszaáll. Nincs Idempotency-Key a létrehozáson — a dupla-submitet a FormSheet `busy` védi.
import { type AssetRowSchema } from '@atlas/contracts';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { AssetRecipientFields } from '@/components/assets/asset-recipient-fields';
import { AssetRequestDetails } from '@/components/assets/asset-request-details';
import { SelectField, TextareaField, TextField } from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Text } from '@/components/ui/text';
import { assetKeys } from '@/features/assets/api';
import { catalogText, useAssetCatalog } from '@/features/assets/catalog';
import {
  buildAssetRequestResolver,
  EMPTY_ASSET_REQUEST_FORM,
  toAssetRequestBody,
  type AssetRequestFormValues,
} from '@/features/assets/form';
import { canEditRequest, requestFormFrom } from '@/features/assets/request-edit';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

type AssetRow = z.infer<typeof AssetRowSchema>;
type DetailResponse = Awaited<ReturnType<typeof api.assetRequests.get.query>>;

export interface AssetRequestSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → LÉTREHOZÁS; egyébként a megnyitott igény azonosítója (nézet / szerkesztés). */
  requestId?: string | null;
  /** A lista-sor (ha van) — az azonnali megjelenítés magja, amíg a by-id lekérés megérkezik. */
  seed?: AssetRow | null;
  /** Szerkesztés módban nyíljon (a sor ceruzája). */
  startInEdit?: boolean;
}

export function AssetRequestSheet({
  open,
  onOpenChange,
  requestId = null,
  seed = null,
  startInEdit = false,
}: AssetRequestSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const canDecide = can('asset_requests.decide');
  const sessionQ = api.session.useQuery(['session'], {});
  const sessionSub = sessionQ.data?.status === 200 ? sessionQ.data.body.sub : undefined;
  const { lookup } = useAssetCatalog();
  const lang = i18n.language;

  const isCreate = requestId === null;
  const detailKey = assetKeys.detail(requestId ?? '');
  const detailQ = api.assetRequests.get.useQuery(
    detailKey,
    { params: { id: requestId ?? '' } },
    { queryKey: detailKey, enabled: open && requestId !== null },
  );
  const fetched = detailQ.data?.status === 200 ? detailQ.data.body : null;
  const request: AssetRow | null = fetched ?? seed;

  // A szerkesztő-állapot: a sheet minden (újra)nyitásakor a `startInEdit` szerint indul.
  const [editing, setEditing] = useEditingState(open, requestId, startInEdit);
  const editable = request !== null && canEditRequest(request, sessionSub, canDecide);
  const formMode = isCreate ? 'create' : 'update';
  const showForm = isCreate || (editing && editable);

  const form = useForm<AssetRequestFormValues>({
    resolver: buildAssetRequestResolver(t, formMode),
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_ASSET_REQUEST_FORM,
  });
  const { reset, handleSubmit, setError, setValue, getValues, watch } = form;
  const category = watch('category');
  const type = watch('type');

  // Előtöltés: szerkesztésnél az igényből, létrehozásnál üres űrlap. A sor azonosítója + verziója a kulcs,
  // hogy egy háttér-frissítés ne írja felül a félig kitöltött űrlapot.
  const rowKey = request ? `${request.id}:${String(request.version)}` : null;
  useEffect(() => {
    if (!open) return;
    if (isCreate) reset(EMPTY_ASSET_REQUEST_FORM);
    else if (request && editing) reset(requestFormFrom(request, lookup));
    // a `request`/`lookup` objektum-azonosságára szándékosan nem figyelünk (rowKey + a katalógus mérete elég)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isCreate, editing, rowKey, lookup.categories.length, reset]);

  // A kategória (UI-szűrő) és a típus összehangolása a KATALÓGUSBÓL: üres kategória → az első; a kategóriába
  // nem tartozó típus → a kategória első típusa. A pillanatnyi értéket `getValues`-ből olvassuk, hogy a fenti
  // előtöltés után ne a régi render-értékkel írjunk felül.
  useEffect(() => {
    if (!showForm) return;
    const cats = lookup.categories.filter((c) => c.types.length > 0);
    const first = cats[0];
    if (!first) return;
    const cat = cats.find((c) => c.key === getValues('category')) ?? first;
    if (cat.key !== getValues('category')) setValue('category', cat.key);
    const current = getValues('type');
    const firstType = cat.types[0];
    if (!cat.types.some((ty) => ty.key === current) && firstType) {
      setValue('type', firstType.key);
    }
  }, [showForm, category, type, lookup.categories, getValues, setValue]);

  const mutation = useEntityMutation<AssetRequestFormValues, DetailResponse>({
    mutationFn: (values) => {
      const body = toAssetRequestBody(values);
      return isCreate
        ? api.assetRequestCreate.mutation({ body })
        : api.assetRequests.update.mutation({
            params: { id: requestId },
            body,
            headers: { 'if-match': String(request?.version ?? '') },
          });
    },
    invalidateKeys: [assetKeys.lists(), ...(isCreate ? [] : [detailKey])],
    ...(isCreate
      ? {}
      : {
          optimistic: {
            queryKey: detailKey,
            update: (old: DetailResponse | undefined, values: AssetRequestFormValues) => {
              if (old?.status !== 200) return old;
              const next = toAssetRequestBody(values);
              return {
                ...old,
                body: {
                  ...old.body,
                  name: next.name,
                  type: next.type,
                  category: lookup.categoryKeyOfType(next.type) ?? old.body.category,
                  netPrice: next.netPrice,
                  vatRate: next.vatRate,
                  website: next.website ?? null,
                  requestNote: typeof next.note === 'string' ? next.note : null,
                  officeUse: next.officeUse === true,
                  usageLocation: typeof next.usageLocation === 'string' ? next.usageLocation : null,
                  // a „kinek" neve a szerver oldalán oldódik fel — az optimista sor csak az azonosítót cseréli
                  requestedForId:
                    next.officeUse === true ? null : (next.requestedForIdentityId ?? null),
                },
              };
            },
          },
        }),
    onFieldErrors: (field, messages) => {
      // A `category` nem wire-mező — a szerver 422-je csak a body-mezőkre jöhet.
      if (field in EMPTY_ASSET_REQUEST_FORM && field !== 'category') {
        setError(field as keyof AssetRequestFormValues, { message: messages.join(' ') });
      }
    },
    messages: {
      success: isCreate ? t('toast:assets.requestCreated') : t('toast:assets.requestUpdated'),
      conflict: t('toast:assets.conflict'),
      forbidden: t('toast:assets.forbidden'),
      validation: t('toast:assets.validation'),
      error: t('toast:assets.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: () => {
        if (isCreate) {
          reset(EMPTY_ASSET_REQUEST_FORM);
          onOpenChange(false);
        } else {
          // szerkesztés után az olvasó-nézet jön vissza (az optimista + újratöltött adattal)
          setEditing(false);
        }
      },
    });
  });

  const categoryOptions = lookup.categories
    .filter((c) => c.types.length > 0)
    .map((c) => ({ value: c.key, label: catalogText(c.name, lang) }));
  const typeOptions = lookup.typesOfCategory(category).map((ty) => ({
    value: ty.key,
    label: catalogText(ty.name, lang),
  }));

  const closeAll = (): void => {
    setEditing(false);
    onOpenChange(false);
  };

  // ── NÉZET mód ──────────────────────────────────────────────────────────────────────────────────────────
  if (!showForm) {
    return (
      <FormSheet
        open={open}
        onOpenChange={(o) => {
          if (!o) closeAll();
          else onOpenChange(true);
        }}
        title={request?.name ?? t('assets.request.viewTitle')}
        description={t('assets.request.viewDesc')}
        footer={
          <>
            <Button variant="outline" type="button" onClick={closeAll}>
              {t('assets.request.close')}
            </Button>
            {editable && (
              <Button
                // KÜLÖN kulcs + preventDefault: a kattintás még ugyanabban az eseményben szerkesztő módba vált, ahol
                // ugyanezen a helyen a `type="submit"` Mentés áll — a React a DOM-gombot újrahasznosítaná, és a
                // böngésző a kattintás alapértelmezett műveletét már beküldésként hajtaná végre (élesben négy
                // véletlen mentés + levél lett belőle)
                key="edit"
                type="button"
                variant="brand"
                onClick={(e) => {
                  e.preventDefault();
                  setEditing(true);
                }}
              >
                <PencilSquareIcon className="mr-2 size-4" />
                {t('assets.edit')}
              </Button>
            )}
          </>
        }
      >
        {request ? (
          <AssetRequestDetails request={request} lookup={lookup} />
        ) : (
          <Text variant="secondary" as="p" className="text-sm">
            {detailQ.isLoading ? t('assets.loading') : t('assets.request.unavailable')}
          </Text>
        )}
      </FormSheet>
    );
  }

  // ── LÉTREHOZÁS / SZERKESZTÉS mód ───────────────────────────────────────────────────────────────────────
  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset(EMPTY_ASSET_REQUEST_FORM);
          closeAll();
        } else onOpenChange(true);
      }}
      title={isCreate ? t('assets.request.createTitle') : t('assets.request.editTitle')}
      description={isCreate ? t('assets.request.createDesc') : request?.name}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => (isCreate ? closeAll() : setEditing(false))}
            disabled={mutation.isPending}
          >
            {t('assets.cancel')}
          </Button>
          <Button key="save" type="submit" variant="brand" disabled={mutation.isPending}>
            {isCreate
              ? mutation.isPending
                ? t('assets.request.submitting')
                : t('assets.request.submit')
              : mutation.isPending
                ? t('assets.saving')
                : t('assets.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField<AssetRequestFormValues>
            name="category"
            label={t('assets.request.fields.category')}
            required
            options={categoryOptions}
            hint={t('assets.request.fields.categoryHint')}
          />
          <SelectField<AssetRequestFormValues>
            name="type"
            label={t('assets.fields.type')}
            required
            options={typeOptions}
          />
        </div>
        <TextField<AssetRequestFormValues>
          name="name"
          label={t('assets.fields.name')}
          required
          placeholder={t('assets.request.fields.namePlaceholder')}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField<AssetRequestFormValues>
            name="netPrice"
            label={t('assets.fields.netPrice')}
            required
            hint={t('assets.fields.netPriceHint')}
          />
          <TextField<AssetRequestFormValues>
            name="vatRate"
            label={t('assets.fields.vatRate')}
            required
            hint={t('assets.request.fields.vatRateHint')}
          />
        </div>
        <TextField<AssetRequestFormValues>
          name="website"
          label={t('assets.request.fields.website')}
          placeholder="https://…"
          hint={t('assets.request.fields.websiteHint')}
        />
        <AssetRecipientFields<AssetRequestFormValues>
          personField="requestedForIdentityId"
          personLabel={t('assets.request.fields.requestedFor')}
          personPlaceholder={t('assets.request.fields.requestedForPlaceholder')}
          personHint={t('assets.request.fields.requestedForHint')}
          initialPerson={
            request?.requestedForId && request.requestedForName
              ? { id: request.requestedForId, label: request.requestedForName }
              : null
          }
        />
        <TextareaField<AssetRequestFormValues>
          name="note"
          label={t('assets.request.fields.note')}
          placeholder={t('assets.request.fields.notePlaceholder')}
          rows={3}
        />
      </Form>
    </FormSheet>
  );
}

/**
 * A szerkesztő-állapot: a sheet minden megnyitásakor (vagy másik igényre váltáskor) a `startInEdit` szerint
 * indul, zárva mindig nézetre áll vissza.
 */
function useEditingState(
  open: boolean,
  requestId: string | null,
  startInEdit: boolean,
): [boolean, (v: boolean) => void] {
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setEditing(open && startInEdit);
  }, [open, requestId, startInEdit]);
  return [editing, setEditing];
}
